import React, { createContext, useMemo, useState } from 'react';
import type { ISearchState, SearchContextProps } from '../types';

const INITIAL_STATE: ISearchState = {
  location: { pickup: null, dropoff: null },
  dates: { pickup: null, dropoff: null },
  time: { pickup: null, dropoff: null },
  differentAgencies: false,
};

export const SearchContext = createContext<SearchContextProps>({
  searchState: INITIAL_STATE,
  updateSearchState: {} as SearchContextProps['updateSearchState'],
});

const SearchProvider = ({ children }: { children: React.ReactNode }) => {
  // PICKUP DATA
  const [pickupLocation, setPickupLocation] = useState<ISearchState['location']['pickup']>(
    INITIAL_STATE.location.pickup,
  );
  const [pickupDate, setPickupDate] = useState<ISearchState['dates']['pickup']>(INITIAL_STATE.dates.pickup);
  const [pickupTime, setPickupTime] = useState<ISearchState['time']['pickup']>(INITIAL_STATE.time.pickup);

  // DROPOFF DATA
  const [dropoffLocation, setDropoffLocation] = useState<ISearchState['location']['dropoff']>(
    INITIAL_STATE.location.dropoff,
  );
  const [dropoffDate, setDropoffDate] = useState<ISearchState['dates']['dropoff']>(INITIAL_STATE.dates.dropoff);
  const [dropoffTime, setDropoffTime] = useState<ISearchState['time']['dropoff']>(INITIAL_STATE.time.dropoff);

  const [differentAgencies, setDifferentAgencies] = useState<ISearchState['differentAgencies']>(
    INITIAL_STATE.differentAgencies,
  );

  const searchState: ISearchState = {
    location: { pickup: pickupLocation, dropoff: dropoffLocation },
    dates: { pickup: pickupDate, dropoff: dropoffDate },
    time: { pickup: pickupTime, dropoff: dropoffTime },
    differentAgencies,
  };

  const updateSearchState: SearchContextProps['updateSearchState'] = {
    location: {
      pickup: (pickup: ISearchState['location']['pickup']) => setPickupLocation(pickup),
      dropoff: (dropoff: ISearchState['location']['dropoff']) => setDropoffLocation(dropoff),
    },
    dates: {
      pickup: (pickup: ISearchState['dates']['pickup']) => setPickupDate(pickup),
      dropoff: (dropoff: ISearchState['dates']['dropoff']) => setDropoffDate(dropoff),
    },
    time: {
      pickup: (pickup: ISearchState['time']['pickup']) => setPickupTime(pickup),
      dropoff: (dropoff: ISearchState['time']['dropoff']) => setDropoffTime(dropoff),
    },
    differentAgencies: (isSelected: ISearchState['differentAgencies']) => setDifferentAgencies(isSelected),
  };

  const props: SearchContextProps = useMemo(
    () => ({ searchState, updateSearchState }),
    [pickupDate, pickupLocation, pickupTime, dropoffDate, dropoffLocation, dropoffTime, differentAgencies],
  );

  return <SearchContext.Provider value={props}>{children}</SearchContext.Provider>;
};

export default SearchProvider;
